import clsx from 'clsx';
import {
  forwardRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { ENVIRONMENT_LABELS, type Environment } from '@visualitics-toolkit/shared';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-white shadow-neon-sm hover:brightness-110',
  secondary: 'border border-border bg-surface-raised text-content hover:border-accent',
  ghost: 'text-muted hover:text-content hover:bg-surface-raised',
  danger: 'bg-severity-irreversible text-slate-950 hover:brightness-110',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', loading = false, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

export function IconButton({
  label,
  children,
  ...props
}: ButtonProps & { label: string }): ReactNode {
  return (
    <Button variant="ghost" aria-label={label} title={label} className="px-2 py-1" {...props}>
      {children}
    </Button>
  );
}

export function Spinner({ className }: { className?: string }): ReactNode {
  return (
    <span
      role="status"
      aria-label="Carregando"
      className={clsx(
        'inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={clsx(
          'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-content',
          'placeholder:text-muted focus:border-accent',
          className,
        )}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={clsx(
          'w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm text-content',
          'placeholder:text-muted focus:border-accent',
          className,
        )}
        {...props}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={clsx(
          'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-content focus:border-accent',
          className,
        )}
        {...props}
      >
        {children}
      </select>
    );
  },
);

export function Checkbox({
  label,
  className,
  disabled,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }): ReactNode {
  return (
    <label
      className={clsx(
        'flex items-start gap-2 text-sm text-content',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <input
        type="checkbox"
        disabled={disabled}
        className="mt-0.5 size-4 accent-[rgb(var(--accent))] disabled:cursor-not-allowed"
        {...props}
      />
      <span>{label}</span>
    </label>
  );
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
  className?: string;
}): ReactNode {
  const tones = {
    neutral: 'border-border text-muted',
    accent: 'border-accent text-accent',
    success: 'border-severity-reversible text-severity-reversible',
    warning: 'border-severity-destructive text-severity-destructive',
    danger: 'border-severity-irreversible text-severity-irreversible',
  } as const;

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Bolinha do ambiente — repetida no banner, no seletor e nos cards de conexão. */
export function EnvDot({ environment }: { environment: Environment }): ReactNode {
  return (
    <span
      data-env={environment}
      title={ENVIRONMENT_LABELS[environment]}
      className="inline-block size-2.5 rounded-full bg-env"
    />
  );
}

export function CopyButton({ value, label = 'Copiar' }: { value: string; label?: string }): ReactNode {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      variant="ghost"
      className="text-xs"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? 'Copiado' : label}
    </Button>
  );
}

export function FileDrop({
  accept,
  onFile,
  onLargeFile,
  maxInlineBytes,
  label,
}: {
  accept: string;
  onFile: (content: string, filename: string) => void;
  onLargeFile?: (file: File) => void;
  maxInlineBytes?: number;
  label: string;
}): ReactNode {
  const [rejectReason, setRejectReason] = useState<string | null>(null);
  const inlineMax = maxInlineBytes ?? 512 * 1024;

  return (
    <div className="space-y-1">
    <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border px-4 py-6 text-sm text-muted hover:border-accent">
      <span>{label}</span>
      <input
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          setRejectReason(null);
          if (file.size > inlineMax) {
            if (onLargeFile) {
              onLargeFile(file);
              return;
            }
            setRejectReason(
              `Arquivo grande demais para colar (${(file.size / 1024 / 1024).toFixed(1)} MiB). Use upload por artifact.`,
            );
            return;
          }
          void file.text().then((content) => onFile(content, file.name));
        }}
      />
    </label>
    {rejectReason ? <p className="text-xs text-severity-irreversible">{rejectReason}</p> : null}
    </div>
  );
}

export function Field({
  label,
  help,
  error,
  htmlFor,
  children,
}: {
  label: string;
  help?: string;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="mb-4">
      <label className="field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-severity-irreversible">{error}</p>
      ) : help ? (
        <p className="field-help">{help}</p>
      ) : null}
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border p-8 text-center">
      <p className="font-medium text-content">{title}</p>
      {description && <p className="max-w-md text-sm text-muted">{description}</p>}
      {action}
    </div>
  );
}
